/**
 * Phase 54.26 — Scanner Button + Modal.
 * The reusable ERP UI element that opens the Scanner Session on
 * first click and shows the QR + connection status. Subscribes to
 * scans via `onScan` (called for every barcode arriving over the
 * WebSocket while this component is mounted).
 *
 * Phase 54.27.1 — Now surfaces the actual `lastError` from the
 * ScannerSession context whenever the connection cannot be
 * established, and exposes a "Yeniden Dene" (Retry) action so the
 * operator is never trapped on an infinite "Bağlanıyor" spinner.
 */
import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, X, Wifi, WifiOff, RotateCcw, AlertCircle } from "lucide-react";
import { useScannerSession } from "@/contexts/ScannerSessionContext";

export default function ScannerButton({ onScan, label = "Telefon Tarayıcı", size = "md" }) {
  const { session, status, mobileConnected, scanCount, mobileUrl, lastError, createSession, disconnect, subscribe, retry } = useScannerSession();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const cbRef = useRef(onScan);
  useEffect(() => { cbRef.current = onScan; }, [onScan]);

  // Subscribe to global barcode events for as long as we're mounted.
  useEffect(() => subscribe((code) => { try { cbRef.current?.(code); } catch (e) { console.error(e); } }), [subscribe]);

  const openModal = async () => {
    setOpen(true);
    if (!session) {
      try { await createSession(); } catch (e) { console.error("session create failed", e); }
    }
  };

  const btnPad = size === "sm" ? "px-2 py-1 text-[10.5px]" : "px-3 py-2 text-[11px]";
  const dot =
    status === "error" || status === "expired" ? "bg-red-500" :
    status === "live" && mobileConnected > 0 ? "bg-green-500" :
    status === "live" ? "bg-[#C8A96A]" :
    status === "connecting" || status === "reconnecting" ? "bg-yellow-500 animate-pulse" :
    "bg-[#A1A1AA]";

  const statusLabel = () => {
    if (status === "error" || status === "expired") return "Hata";
    if (mobileConnected > 0) return <><Wifi size={11} className="text-green-500" /> Bağlı</>;
    if (status === "live") return "Hazır";
    if (status === "reconnecting") return "Yeniden Bağlanıyor…";
    if (status === "connecting") return "Bağlanıyor…";
    return <><WifiOff size={11} /> Beklemede</>;
  };

  const isFailed = status === "error" || status === "expired";

  return (
    <>
      <button
        type="button"
        data-testid="scanner-open-btn"
        onClick={openModal}
        title={label}
        className={`inline-flex items-center gap-1.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] ${btnPad} uppercase tracking-[0.12em]`}
      >
        <Smartphone size={13} />
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      </button>

      {open && (
        <div data-testid="scanner-modal" className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-white w-full max-w-sm border border-[#E7E5E4] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7E5E4]">
              <h3 className="font-heading text-lg">Telefon Tarayıcı</h3>
              <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-[#FAFAF9]"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              {isFailed && (
                <div data-testid="scanner-error" className="bg-red-50 border border-red-200 p-3 flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium text-red-800">Bağlantı Başarısız</div>
                    <div className="text-[11px] text-red-700 mt-1 break-words">{lastError || "Bilinmeyen hata."}</div>
                    <button
                      type="button"
                      data-testid="scanner-retry-btn"
                      onClick={() => retry()}
                      className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-red-600 hover:bg-red-700 text-white px-3 py-1.5"
                    >
                      <RotateCcw size={11} /> Yeniden Dene
                    </button>
                  </div>
                </div>
              )}
              {!isFailed && session && mobileUrl ? (
                <>
                  <p className="text-[11px] text-[#71717A] leading-relaxed">
                    Telefonunun kamerasıyla QR kodu tara. Bağlandıktan sonra bütün taramalar bu ERP sekmesine düşer.
                  </p>
                  <div className="flex items-center justify-center bg-white p-4 border border-[#F5F5F4]">
                    <QRCodeSVG data-testid="scanner-qr" value={mobileUrl} size={220} level="M" />
                  </div>
                  <div className="text-[10px] font-mono text-[#71717A] break-all bg-[#FAFAF9] p-2 border border-[#F5F5F4] flex items-center gap-2">
                    <span className="flex-1 truncate">{mobileUrl}</span>
                    <button
                      onClick={async () => { try { await navigator.clipboard.writeText(mobileUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } }}
                      className="text-[9px] uppercase tracking-wider text-[#52525B] hover:text-[#0A0A0A] shrink-0"
                    >{copied ? "✓" : "Kopyala"}</button>
                  </div>
                  {status === "reconnecting" && lastError && (
                    <div data-testid="scanner-reconnect-note" className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5">
                      {lastError}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-[0.15em] text-[#71717A]">
                    <div className="bg-[#FAFAF9] py-2">
                      <div className="text-[10px]">Durum</div>
                      <div className="text-[12px] text-[#0A0A0A] flex items-center justify-center gap-1 mt-0.5" data-testid="scanner-status">
                        {statusLabel()}
                      </div>
                    </div>
                    <div className="bg-[#FAFAF9] py-2">
                      <div className="text-[10px]">Cihaz</div>
                      <div className="text-[12px] text-[#0A0A0A] font-mono mt-0.5" data-testid="scanner-device-count">{mobileConnected}</div>
                    </div>
                    <div className="bg-[#FAFAF9] py-2">
                      <div className="text-[10px]">Tarama</div>
                      <div className="text-[12px] text-[#0A0A0A] font-mono mt-0.5" data-testid="scanner-scan-count">{scanCount}</div>
                    </div>
                  </div>
                </>
              ) : !isFailed ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 mx-auto animate-spin border-2 border-[#C8A96A] border-t-transparent rounded-full" />
                  <p className="text-[11px] text-[#71717A] mt-3">Oturum oluşturuluyor…</p>
                </div>
              ) : null}
            </div>
            <div className="border-t border-[#E7E5E4] px-5 py-3 flex justify-between gap-2">
              <button
                type="button"
                onClick={async () => { await disconnect(); setOpen(false); }}
                disabled={!session}
                data-testid="scanner-disconnect-btn"
                className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-red-600 disabled:opacity-40 inline-flex items-center gap-1"
              ><RotateCcw size={11} /> Bağlantıyı Kes</button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 bg-[#0A0A0A] text-white"
              >Kapat</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
